<template>
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h5 class="mb-0">Registry Connections</h5>
    <button
      class="btn btn-success btn-sm"
      @click="openCreateModal"
      :disabled="!selectedCompany"
    >
      <i class="fas fa-plus me-1"></i>
      Create Connection
    </button>
  </div>

  <LoadingIndicatorComponent v-if="loadingConnections" />
  <table
    v-else-if="registryConnections && registryConnections.length > 0"
    class="table table-hover bg-white my-2"
  >
    <thead>
      <tr>
        <th>Integration Name</th>
        <th>Registry Company ID</th>
        <th>Registry EECS</th>
        <th>Region</th>
        <th>Type</th>
        <th>Status</th>
        <th>Transaction log</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="connection in registryConnections" :key="connection.id">
        <td class="px-2">
          {{ formatIntegrationName(connection.integration_name) }}
        </td>
        <td>
          {{ connection.registry_company_id || "Not configured" }}
        </td>
        <td>{{ connection.registry_eecs || "N/A" }}</td>
        <td>{{ connection.region }}</td>
        <td>
          <span
            class="badge"
            :class="{
              'bg-primary': connection.connection_type === 'STANDARD',
              'bg-dark-subtle': connection.connection_type === 'VIRTUAL',
            }"
          >
            {{
              connection.connection_type === "VIRTUAL" ? "Virtual" : "Standard"
            }}
          </span>
        </td>
        <td>
          <span
            class="badge"
            :class="{
              'bg-success': connection.is_active,
              'bg-warning': !connection.is_active,
            }"
          >
            {{ connection.is_active ? "Active" : "Inactive" }}
          </span>
        </td>
        <td>
          <span
            class="badge"
            :class="
              transactionLogApplies(connection) ? 'bg-success' : 'bg-secondary'
            "
            :title="transactionLogOffReason(connection) ?? undefined"
          >
            {{ transactionLogApplies(connection) ? "On" : "Off" }}
          </span>
        </td>
        <td>
          <button
            class="btn btn-primary btn-sm me-2"
            @click="syncRegistryConnection(connection.id)"
            :disabled="
              !connection.is_active || syncingConnectionId === connection.id
            "
          >
            <span
              v-if="syncingConnectionId === connection.id"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            Sync
          </button>
          <button
            class="btn btn-outline-secondary btn-sm me-2"
            @click="openEditModal(connection)"
          >
            Edit
          </button>
          <button
            class="btn btn-danger btn-sm"
            @click="openDeleteConfirmation(connection)"
            :disabled="deletingConnectionId === connection.id"
          >
            <span
              v-if="deletingConnectionId === connection.id"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <font-awesome-icon icon="fas fa-trash" />
          </button>
        </td>
      </tr>
    </tbody>
  </table>
  <p v-else class="text-muted">
    No registry connections configured for this company
  </p>

  <!-- Registry Connection Edit/Create Modal -->
  <EditRegistryConnectionModal
    v-if="selectedCompany?.id"
    v-model="showEditModal"
    :connection="selectedConnection"
    :companyId="selectedCompany.id"
    :mode="modalMode"
    @connection-updated="handleConnectionUpdated"
    @connection-created="handleConnectionCreated"
  />

  <!-- Delete Confirmation Modal -->
  <ConfirmationModalComponent
    :visible="showDeleteConfirmation"
    title="Confirm Delete"
    message="Are you sure you want to delete this registry connection?"
    @confirm="confirmDelete"
    @cancel="closeDeleteConfirmation"
  >
    <div v-if="connectionToDelete" class="alert alert-warning">
      <strong>Integration:</strong>
      {{ formatIntegrationName(connectionToDelete.integration_name) }}
      <br />
      <strong>Region:</strong> {{ connectionToDelete.region }}
    </div>
    <p class="text-muted mb-0">This action cannot be undone.</p>
  </ConfirmationModalComponent>
</template>

<script setup lang="ts">
import { useEventBus } from "@vueuse/core";
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import EditRegistryConnectionModal from "@/dashboard/admin/EditRegistryConnectionModal.vue";
import {
  formatIntegrationName,
  transactionLogApplies,
  transactionLogOffReason,
} from "@/registryUtils";
import { registryConnectionsChanged } from "@/registryEvents";
import type { Company, RegistryConnection } from "@/types";
import { ref, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

interface Props {
  selectedCompany: Company | null;
}

const props = defineProps<Props>();
const connectionChanges = useEventBus(registryConnectionsChanged);

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

// Registry connections state
const registryConnections = ref<RegistryConnection[]>([]);
const loadingConnections = ref(false);
const syncingConnectionId = ref<number | null>(null);
const showEditModal = ref(false);
const selectedConnection = ref<RegistryConnection | null>(null);
const modalMode = ref<"create" | "edit">("edit");
const showDeleteConfirmation = ref(false);
const connectionToDelete = ref<RegistryConnection | null>(null);
const deletingConnectionId = ref<number | null>(null);

// Fetch registry connections function
const fetchRegistryConnections = async () => {
  if (!props.selectedCompany?.id) {
    registryConnections.value = [];
    return;
  }

  loadingConnections.value = true;
  try {
    const response = await goSolidApi.get(
      `/api/admin/companies/${props.selectedCompany.id}/registry-connections`
    );
    registryConnections.value = response.data.data || [];
  } catch (error: unknown) {
    globalErrorHandler(error);
    registryConnections.value = [];
  } finally {
    loadingConnections.value = false;
  }
};

// Sync individual registry connection
const syncRegistryConnection = async (connectionId: number) => {
  if (!props.selectedCompany?.id) return;

  syncingConnectionId.value = connectionId;
  try {
    const response = await goSolidApi.post(
      `/api/admin/companies/${props.selectedCompany.id}/registry-connections/${connectionId}/sync`
    );
    if (response.data.success) {
      globalSuccessHandler(
        "Sync Started",
        "Registry connection sync started successfully."
      );
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    syncingConnectionId.value = null;
  }
};

const openCreateModal = () => {
  selectedConnection.value = null;
  modalMode.value = "create";
  showEditModal.value = true;
};

const openEditModal = (connection: RegistryConnection) => {
  selectedConnection.value = connection;
  modalMode.value = "edit";
  showEditModal.value = true;
};

const handleConnectionCreated = async () => {
  await fetchRegistryConnections();
  if (props.selectedCompany) connectionChanges.emit(props.selectedCompany.id);
};

const handleConnectionUpdated = async () => {
  await fetchRegistryConnections();
  if (props.selectedCompany) connectionChanges.emit(props.selectedCompany.id);
};

const openDeleteConfirmation = (connection: RegistryConnection) => {
  connectionToDelete.value = connection;
  showDeleteConfirmation.value = true;
};

const closeDeleteConfirmation = () => {
  connectionToDelete.value = null;
  showDeleteConfirmation.value = false;
};

const confirmDelete = async () => {
  if (!props.selectedCompany?.id || !connectionToDelete.value) return;

  deletingConnectionId.value = connectionToDelete.value.id;
  try {
    await goSolidApi.delete(
      `/api/admin/companies/${props.selectedCompany.id}/registry-connections/${connectionToDelete.value.id}`
    );
    await fetchRegistryConnections();
    connectionChanges.emit(props.selectedCompany.id);
    closeDeleteConfirmation();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    deletingConnectionId.value = null;
  }
};

// Keyed on the id, not the object: the account store hands over new company objects with every
// answer it takes, and the connections are read by the company's id alone.
watch(() => props.selectedCompany?.id, fetchRegistryConnections, {
  immediate: true,
});
</script>
